import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { Observable, retry, catchError } from "rxjs";
import { Farm } from "../models/farm";
import { BaseService } from "./base.service";
import { Field } from "../models/field";
import { History } from "../models/history";
import { BoundingBox } from "../models/boundingBox";
import { Pin } from "../models/pin";
import { FieldAdmin } from "../models/fieldAdmin";
import { FarmNotifications } from "../models/farmNotifications";
import { TenantViewModel } from "../models/tenant-view-models/tenant-view-model";


@Injectable({
    providedIn: 'root'
  })
  export class FarmManagementService extends BaseService {

    private API_FARM_MANAGEMENT_URL = this.API_BASE_URL + "farm-management"; 

    constructor(private httpClient: HttpClient,
        public _snackBar: MatSnackBar,
        public cookieService: CookieService,
        public translate: TranslateService) {
            super()
      }

    ////////////////////////////////////////////////////////////////////////////
    //////////////////                GET                     //////////////////
    ////////////////////////////////////////////////////////////////////////////

    // getTenantFarmFields(): Observable<TenantFarmField> {
      getTenantFarmFields(tenantId: number): Observable<TenantViewModel> {
      return this.httpClient.get<any>(this.API_FARM_MANAGEMENT_URL + `/tenant/${tenantId}/farmfields`)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open('Failed to retrieve farm fields for this tenant', this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }


    getFarmNotificationStatus(farmId: number): Observable<any> {
        return this.httpClient.get<any>(this.API_FARM_MANAGEMENT_URL + "/farm/receiveNotifications?farmId=" + farmId)
          .pipe(
            //catchError(this.errorHandler)
            catchError(err => {
              this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.NOTIFICATION-STATUS'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

    getFieldAdminData(tenantId: number): Observable<FieldAdmin[]> {
        return this.httpClient.get<FieldAdmin[]>(this.API_FARM_MANAGEMENT_URL + "/farm/fieldAdmin?tenantId=" + tenantId)
          .pipe(
            retry(1),
            catchError(err => {
              this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.FIELD-ADMIN'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

    getTenantFarms(tenantId: number): Observable<Farm[]> {
        if(tenantId !== undefined && tenantId !== null){
            // //
            return this.httpClient.get<Farm[]>(this.API_FARM_MANAGEMENT_URL + "/farm/farms?tenantId=" + tenantId)
                .pipe(
                    retry(2),
                    catchError(err => {
                        this.hasError.next(true);
                        this._snackBar.open(this.translate.instant('DATA-SERVICE.FARMS'), this.translate.instant('SNACKBAR.DISMISS'), {
                            duration: 5000,
                        });
                        this.hasError.next(false);
                        return this.errorHandler(err);
                    })
                );
        }

      }

      getFarmPolygon(farmId: number): Observable<Field[]> {
        return this.httpClient.get<Field[]>(this.API_FARM_MANAGEMENT_URL + "/farm/fields?farmId=" + farmId)
          .pipe(
            retry(1),
            catchError(err => {
              this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.FIELDS'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

      getFarmFields(farmId: number): Observable<Field[]> {
        return this.httpClient.get<Field[]>(this.API_FARM_MANAGEMENT_URL + "/farm/fieldsRaster?farmId=" + farmId)
          .pipe(
            retry(1),
            catchError(err => {
              this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.FIELDS'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

      getFarmBoundingBox(farmId: number): Observable<BoundingBox> {
        return this.httpClient.get<BoundingBox>(this.API_FARM_MANAGEMENT_URL + "/farm/boundingBox?farmId=" + farmId)
          .pipe(
            retry(1),
            catchError(err => {
              //this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.ZOOM'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              //this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

      getFarmHistory(startDate: string, endDate: string, farmId: number): Observable<History[]> {
        return this.httpClient.get<History[]>(this.API_FARM_MANAGEMENT_URL + "/farm/farmHistory?farmId=" + farmId + "&startDate=" + startDate + "&endDate=" + endDate)
          .pipe(
            retry(1),
            catchError(err => {
              //this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.HISTORY'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              //this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

      getSavedPins(fieldId: number): Observable<Pin[]> {
        return this.httpClient.get<Pin[]>(this.API_FARM_MANAGEMENT_URL + "/farm/allPins?fieldId=" + fieldId)
          .pipe(
            retry(1),
            catchError(err => {
              this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.SAVED-PINS'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             INSERT                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    insertPin(pin: Pin): Observable<any> {
        return this.httpClient.post<any>(this.API_FARM_MANAGEMENT_URL + "/farm/insertPin", pin)
          .pipe(
            catchError(err => {
              this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.INSERT-PIN'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             UPDATE                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    updatePin(pin: Pin): Observable<any> {
        return this.httpClient.put<any>(this.API_FARM_MANAGEMENT_URL + "/farm/updatePin", pin)
          .pipe(
            catchError(err => {
              this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-PIN'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }

    updateFarmNotificationStatus(farmNotifications: FarmNotifications): Observable<any> {
        return this.httpClient.put<any>(this.API_FARM_MANAGEMENT_URL + "/farm/receiveNotifications", farmNotifications)
            .pipe(
            catchError(err => {
                this.hasError.next(true);
                this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-NOTIFICATION-STATUS'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
            })
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             DELETE                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    deletePin(id: number): Observable<any> {
        return this.httpClient.delete<any>(this.API_FARM_MANAGEMENT_URL + "/farm/deletePinByQueryParam?id=" + id)
          .pipe(
            catchError(err => {
              this.hasError.next(true);
              this._snackBar.open(this.translate.instant('DATA-SERVICE.DELETE-PIN'), this.translate.instant('SNACKBAR.DISMISS'), {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
          );
      }
    
  }  
